import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  Message as MessageIcon,
  Send as SendIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const Teachers = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('student-portal/teachers');
      if (response.data.success) {
        setTeachers(response.data.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch teachers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!subject.trim() || !message.trim()) {
      enqueueSnackbar('Please fill in both subject and message', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('student-portal/send-message', {
        teacher_id: selectedTeacher.teacher_id,
        subject: subject.trim(),
        message: message.trim()
      });

      if (response.data.success) {
        enqueueSnackbar('Message sent successfully', { variant: 'success' });
        setMessageDialog(false);
        setSelectedTeacher(null);
        setSubject('');
        setMessage('');
      }
    } catch (error) {
      enqueueSnackbar('Failed to send message', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const TeacherCard = ({ teacher }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                mr: 2,
                bgcolor: 'primary.main',
                fontSize: '1.5rem'
              }}
            >
              {teacher.first_name?.charAt(0)}{teacher.last_name?.charAt(0)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {teacher.first_name} {teacher.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {teacher.qualification}
              </Typography>
              <Chip 
                label={teacher.specialization || 'General'} 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <SchoolIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">
                <strong>Subjects:</strong> {teacher.subjects || 'Not assigned'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">
                {teacher.email || 'No email provided'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PhoneIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">
                {teacher.phone || 'No phone provided'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<MessageIcon />}
              onClick={() => {
                setSelectedTeacher(teacher);
                setMessageDialog(true);
              }}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              Send Message
            </Button>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        My Teachers
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {teachers.length > 0 ? (
        <Grid container spacing={3}>
          {teachers.map((teacher) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={teacher.teacher_id}>
              <TeacherCard teacher={teacher} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert severity="info">
          No teachers assigned to your class yet. Please contact the administration for more information.
        </Alert>
      )}

      {/* Message Dialog */}
      <Dialog 
        open={messageDialog} 
        onClose={() => setMessageDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            Send Message to {selectedTeacher?.first_name} {selectedTeacher?.last_name}
          </Box>
          <IconButton onClick={() => setMessageDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                {selectedTeacher?.first_name?.charAt(0)}{selectedTeacher?.last_name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {selectedTeacher?.first_name} {selectedTeacher?.last_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedTeacher?.qualification} • {selectedTeacher?.specialization}
                </Typography>
              </Box>
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Enter message subject..."
          />

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message here..."
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Please be respectful and professional in your communication. 
              Your teacher will receive this message and may respond via email or during class.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setMessageDialog(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={sendMessage}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={loading || !subject.trim() || !message.trim()}
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Stats */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Teacher Statistics</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {teachers.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Teachers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="success.main" fontWeight="bold">
                  {teachers.filter(t => t.subjects && t.subjects.split(',').length > 1).length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Multi-Subject Teachers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="warning.main" fontWeight="bold">
                  {teachers.filter(t => t.email).length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Available for Email
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Teachers;
